/**
 * The identity workbench's state, derivations and write path.
 *
 * FD-507 lifted this out of `IdentityMappingsSection.tsx` so the queue, the
 * inspector and the bulk review R-FD-053 adds have somewhere to land. Behaviour
 * is unchanged, line for line: the staged-edit model, the freeze shaping, the
 * two-call save and every comment explaining why each is the way it is moved
 * together, and the 1675 lines of tests over them pass unedited.
 *
 * It returns one object rather than fifteen callbacks because the pieces that
 * consume it — toolbar, tabs, table — each want a different slice of the same
 * state, and threading them individually is how the two halves of the freeze
 * shaping drift apart.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getIdentityMappings,
  normalizeSid,
  removeIdentityMappings,
  setIdentityMappings,
} from '../api/identity';
import {
  D2CMappingSubmission,
  IdentityMappingView,
  IdentitySuggestionUser,
  MappingSubmission,
  SuggestionCandidate,
  TenantUser,
} from '../types/identity';
import { invalidateIdentityQueries } from '../lib/identityQueryKeys';
import { useIdentitySuggestions, useSuggestionRows } from './useIdentitySuggestions';
import {
  DeviceActionResult,
  DeviceActionResultRow,
} from '../components/BulkActionResultView';
import { PendingSource } from '../components/identity/AddMappingPanel';
import { deriveMappingChipStatus } from '../components/identity/MappingStatusChip';
import { areMappingsFrozen } from '../components/identity/MappingsFrozenBanner';
import {
  FILTER_LABELS,
  IdentityDetail,
  FILTER_ORDER,
  RowFilter,
  RowVM,
  StagedEdit,
  matchesFilter,
  matchesRowFilter,
  normalizeEchoKey,
  rowLabel,
  submissionKey,
} from '../components/identity/identityRowModel';

// FD-508. The floor matches what the server ignores (FD-506), so the client
// never sends a term the server would discard; the debounce matches
// `TenantUserSearchPanel`'s, because both are a person typing into a box.
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * FD-510 — how many mapping rows go in one request.
 *
 * `Invoke-IdentityMappingUpsert` performs one or two Graph point reads plus a
 * table read plus a table write PER ROW, SEQUENTIALLY, and there is **no
 * maximum batch size anywhere in the backend** — the only validation is
 * `Count -eq 0`. A single body of a thousand submissions therefore runs into
 * Azure's ~230 s front-end idle timeout with no guard, and the request dies
 * holding the only record of what it had already written.
 *
 * Chunking is the client-side half of that. Each chunk is independently
 * idempotent (`Add-AzTableRow -UpdateExisting`), so a retry of a chunk that did
 * land is harmless, and a chunk that fails leaves the ones before it written
 * and the ones after it staged.
 */
const SAVE_CHUNK_SIZE = 100;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export interface UseIdentityWorkbenchParams {
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  bprtStatus?: string | null;
}

// FD-513 — DRAIN THE PAGES. This read used to be `{ top: 100 }` with the
// continuation token DISCARDED, so a campaign with more than 100 saved mappings
// silently lost every orphan-mapping row past the first page. Silently is the
// operative word: there was no "Load more", no count, and no caveat — the rows
// simply were not in the queue, and a mapping you cannot see is a mapping you
// cannot correct or remove.
//
// Draining is the right shape here rather than a "Load more". These rows are a
// SINGLE `IdentityMappings` partition (PK = migrationId) on an EasyAuth-gated
// page an operator opened deliberately — the ADR-013 posture — and they are
// folded into a queue whose own paging is the suggestions feed's. A second,
// differently-paged list inside one table would be a scroll position an
// operator cannot reason about.
//
// THE CAP IS NOT A SILENT TRUNCATION. At `MAX_MAPPING_PAGES` the drain stops
// and reports `truncated: true`, which the queue surfaces. A loop bounded only
// by the server's own token is a loop a server bug can make infinite; a cap
// that hides what it dropped is the defect this task exists to fix, committed
// a second time.
const MAPPING_PAGE_SIZE = 100;
const MAX_MAPPING_PAGES = 50;

/**
 * What a single-row write came back as. A discriminated union rather than a
 * boolean because "rejected" is an ANSWER — the request landed and the server
 * declined the row — while a thrown error is a failure to get an answer, and
 * the inspector renders those two differently.
 */
export type SaveOneOutcome =
  | { kind: 'accepted' | 'updated' | 'removed' | 'notFound' }
  | { kind: 'rejected'; reason: string };

export interface DrainedMappings {
  mappings: IdentityMappingView[];
  /** True when the cap stopped the drain before the server ran out of pages. */
  truncated: boolean;
}

async function drainIdentityMappings(migrationId: string): Promise<DrainedMappings> {
  const mappings: IdentityMappingView[] = [];
  let token: string | undefined;
  for (let page = 0; page < MAX_MAPPING_PAGES; page += 1) {
    const data = await getIdentityMappings(migrationId, {
      top: MAPPING_PAGE_SIZE,
      continuationToken: token,
    });
    mappings.push(...(data.mappings ?? []));
    token = data.continuationToken ?? undefined;
    if (!token) return { mappings, truncated: false };
  }
  return { mappings, truncated: true };
}

export function useIdentityWorkbench({
  migrationId,
  migrationType,
  bprtStatus,
}: UseIdentityWorkbenchParams) {
  const queryClient = useQueryClient();
  const isT2T = migrationType === 'T2T';
  const frozen = areMappingsFrozen(bprtStatus);

  // URL-held view state is read before the queries because the search term is
  // one of the query's inputs.
  const [searchParams, setSearchParams] = useSearchParams();

  // FD-508 — the queue search. `?q=` for the same reason the filter and the
  // open row are URL state (ADR-015's surviving rule, restated by FD-ADR-043):
  // a narrowed queue is linkable, survives a reload, and is never localStorage.
  //
  // Two values, not one. `searchInput` is what the operator is typing and what
  // the box renders; `search` is what reaches the server, debounced, so a
  // twelve-character name is one request rather than twelve. Below a
  // two-character floor nothing is sent at all — the server ignores a shorter
  // term anyway (FD-506), and a request whose answer is "everything" is one
  // worth not making.
  const searchInput = searchParams.get('q') ?? '';
  const setSearchInput = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('q', next);
    else params.delete('q');
    setSearchParams(params, { replace: true });
  };
  const [search, setSearch] = useState(() =>
    searchInput.trim().length >= MIN_SEARCH_LENGTH ? searchInput.trim() : '',
  );
  useEffect(() => {
    const trimmed = searchInput.trim();
    const next = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    if (next === search) return;
    const timer = setTimeout(() => setSearch(next), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  const query = useIdentitySuggestions(migrationId, true, search);
  const { rows, devicesWithoutManifest, totalUniqueUsers, summary, matchedUserCount } =
    useSuggestionRows(query);

  // FD-387 — the saved mappings, read directly rather than inferred from the
  // suggestions feed. Without this a mapping for a user whose devices have not
  // enrolled is written and then unreachable, which is exactly what the manual
  // add flow produces.
  const mappingsQuery = useQuery({
    queryKey: ['identity-mappings', migrationId],
    queryFn: () => drainIdentityMappings(migrationId),
  });

  const [staged, setStaged] = useState<Map<string, StagedEdit>>(new Map());
  const [showAddPanel, setShowAddPanel] = useState(false);
  // FD-384 — URL param, never localStorage (ADR-015's surviving rule), so a
  // filtered view is linkable. Namespaced `identity` because Resources already
  // carries `?migrationId=` and `?notReady=` for the inventory beneath it.
  const rawFilter = searchParams.get('identityFilter');
  const filter: RowFilter =
    rawFilter && rawFilter in FILTER_LABELS ? (rawFilter as RowFilter) : 'action';
  const setFilter = (next: RowFilter) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'action') params.delete('identityFilter');
    else params.set('identityFilter', next);
    setSearchParams(params, { replace: true });
  };
  // FD-495 — the open drawer row, in the URL for the same reason the filter is
  // (ADR-015's surviving rule, restated by FD-ADR-043): UI state is linkable
  // and survives a reload, and it is never `localStorage`. Namespaced
  // `identityRow` so it cannot collide with `?migrationId=` / `?notReady=`,
  // which `Resources › Identities` already carries beneath this same component
  // (FD-ADR-037 §1 — one workspace, mounted twice).
  const selectedRowKey = searchParams.get('identityRow');
  const setSelectedRowKey = (next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('identityRow', next);
    else params.delete('identityRow');
    setSearchParams(params, { replace: true });
  };
  // FD-384 — a device expanded to its serials. Same inline-disclosure choice as
  // the candidate chooser, and for the same reason: no Fluent portal.
  const [expandedDevices, setExpandedDevices] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<DeviceActionResult | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  // FD-382 — rows whose alternate candidates are revealed. The server ranks and
  // returns up to five; until now the UI rendered index 0 and discarded the
  // rest, so reaching the second-ranked candidate meant opening the picker and
  // searching for a user the server had already named.
  const [expandedAlternates, setExpandedAlternates] = useState<Set<string>>(new Set());
  // FD-383 — multi-select, on the same DataTableSelection primitive the device
  // grid uses. Keyed on RowVM.key (normalized SID, or `manual:<id>`).
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // What a bulk action DID NOT do. Reported rather than silently dropped: a
  // button that says "Approve 8 selected" and stages 5 is worse than one that
  // says which 3 it could not, because the operator has no way to tell.
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  // Per-type submit row (FD-103 v1.1): T2T {sourceUserId, destUserId,
  // sourceUserSid?} — requires the server-resolved source objectId; D2C
  // {sourceUserSid, destUserId}. Returns null when a T2T row has no resolved
  // source identity (nothing valid to submit; the manual Add-mapping flow
  // covers those users).
  const buildSubmission = (
    row: IdentitySuggestionUser,
    destUser: TenantUser,
  ): MappingSubmission | null => {
    if (isT2T) {
      if (!row.resolvedSourceUser) return null;
      return {
        sourceUserId: row.resolvedSourceUser.id,
        destUserId: destUser.id,
        sourceUserSid: normalizeSid(row.sid),
      };
    }
    const d2c: D2CMappingSubmission = {
      sourceUserSid: normalizeSid(row.sid),
      destUserId: destUser.id,
    };
    return d2c;
  };

  const stageEdit = (key: string, edit: StagedEdit) => {
    setStaged((prev) => {
      const next = new Map(prev);
      next.set(key, edit);
      return next;
    });
  };

  const unstage = (key: string) => {
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const discardStaged = () => setStaged(new Map());

  // "Accept N exact matches" — N counts rows carrying the SERVER-computed
  // autoAcceptEligible verdict that are not already staged.
  const acceptableRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.autoAcceptEligible &&
          r.suggestions.length > 0 &&
          !staged.has(normalizeSid(r.sid)) &&
          (!isT2T || Boolean(r.resolvedSourceUser)),
      ),
    [rows, staged, isT2T],
  );

  const handleAcceptExactMatches = () => {
    setStaged((prev) => {
      const next = new Map(prev);
      for (const row of acceptableRows) {
        const key = normalizeSid(row.sid);
        const candidate = row.suggestions[0];
        const submission = buildSubmission(row, candidate.user);
        if (!submission) continue;
        next.set(key, {
          kind: 'set',
          label: rowLabel(row),
          submission,
          destUser: candidate.user,
          matchedOn: candidate.matchedOn,
        });
      }
      return next;
    });
  };

  // FD-382 (FD-ADR-037 §2) — approve ONE row, on the candidate the operator is
  // looking at. This deliberately does not consult `autoAcceptEligible`.
  //
  // That verdict is server-computed and authoritative, and it stays the sole
  // gate on the campaign-wide sweep above, which is what it was built for: an
  // unattended bulk accept must not promote a fuzzy name match. It was never a
  // claim that a human reading a labelled `UPN`-tier candidate is unqualified
  // to accept it — and treating it as one is exactly what made four of the five
  // match tiers unreachable, since the only escape was to open the picker and
  // search for the user the suggestion had already named.
  //
  // Nothing here bypasses SERVER validation: this still writes through
  // set-identity-mappings, which re-resolves the destination user against Graph
  // and can reject the row. What is bypassed is a client-side eligibility
  // filter, not a control.
  const handleApprove = (row: IdentitySuggestionUser, candidate: SuggestionCandidate) => {
    const submission = buildSubmission(row, candidate.user);
    if (!submission) return;
    stageEdit(normalizeSid(row.sid), {
      kind: 'set',
      label: rowLabel(row),
      submission,
      destUser: candidate.user,
      matchedOn: candidate.matchedOn,
    });
    // Collapse the chooser once a choice is made — the staged value is now the
    // row's answer, and leaving alternates open invites a second click that
    // would silently overwrite it.
    setExpandedAlternates((prev) => {
      if (!prev.has(normalizeSid(row.sid))) return prev;
      const next = new Set(prev);
      next.delete(normalizeSid(row.sid));
      return next;
    });
  };

  // FD-383 — the selected rows, resolved back to their suggestion rows. Manual
  // rows are already staged by construction, so they are not bulk targets.
  const selectedSuggestionRows = useMemo(
    () => rows.filter((r) => selectedKeys.has(normalizeSid(r.sid))),
    [rows, selectedKeys],
  );

  const approvableSelected = useMemo(
    () =>
      selectedSuggestionRows.filter(
        (r) =>
          r.suggestions.length > 0 &&
          !r.existingMapping &&
          (!isT2T || Boolean(r.resolvedSourceUser)),
      ),
    [selectedSuggestionRows, isT2T],
  );

  const removableSelected = useMemo(
    () => selectedSuggestionRows.filter((r) => Boolean(r.existingMapping)),
    [selectedSuggestionRows],
  );

  const selectedCount = selectedKeys.size;

  // FD-381 — the D2C add panel's left-hand list: source identities the devices
  // actually reported that have no mapping and no staged edit yet. Drawn from
  // the suggestions feed, so it costs no extra call.
  const unmappedObservedSources = useMemo(
    () => rows.filter((r) => !r.existingMapping && !staged.has(normalizeSid(r.sid))),
    [rows, staged],
  );

  const handleApproveSelected = () => {
    const approved = approvableSelected.length;
    setStaged((prev) => {
      const next = new Map(prev);
      for (const row of approvableSelected) {
        const candidate = row.suggestions[0];
        const submission = buildSubmission(row, candidate.user);
        if (!submission) continue;
        next.set(normalizeSid(row.sid), {
          kind: 'set',
          label: rowLabel(row),
          submission,
          destUser: candidate.user,
          matchedOn: candidate.matchedOn,
        });
      }
      return next;
    });

    const skipped = selectedSuggestionRows.length - approved;
    setBulkNotice(
      skipped > 0
        ? `Staged ${approved} of ${selectedSuggestionRows.length} selected. ${skipped} skipped — already mapped, no candidate, or source identity unresolved.`
        : `Staged ${approved} approval${approved === 1 ? '' : 's'}.`,
    );
    setSelectedKeys(new Set());
  };

  const handleRemoveSelected = () => {
    const removed = removableSelected.length;
    setStaged((prev) => {
      const next = new Map(prev);
      for (const row of removableSelected) {
        const mapping = row.existingMapping!;
        const sourceKey =
          mapping.sourceUserId ?? mapping.sourceUserSid ?? normalizeSid(row.sid);
        next.set(normalizeSid(row.sid), {
          kind: 'remove',
          label: rowLabel(row),
          sourceKey,
        });
      }
      return next;
    });

    const skipped = selectedSuggestionRows.length - removed;
    setBulkNotice(
      skipped > 0
        ? `Staged ${removed} of ${selectedSuggestionRows.length} selected for removal. ${skipped} skipped — no mapping to remove.`
        : `Staged ${removed} removal${removed === 1 ? '' : 's'}.`,
    );
    setSelectedKeys(new Set());
  };

  const toggleAlternates = (key: string) => {
    setExpandedAlternates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDevices = (key: string) => {
    setExpandedDevices((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleRemove = (row: IdentitySuggestionUser) => {
    const mapping = row.existingMapping;
    if (!mapping) return;
    // Remove keys are GUIDs (T2T rows) or normalized SIDs (D2C rows) per the
    // FD-103 sourceKeys contract; D2C rows have null sourceUserId.
    const sourceKey =
      mapping.sourceUserId ?? mapping.sourceUserSid ?? normalizeSid(row.sid);
    stageEdit(normalizeSid(row.sid), { kind: 'remove', label: rowLabel(row), sourceKey });
  };

  // FD-381 — stage a manual mapping from the two-panel add. The submit row is
  // per-type (FD-103 v1.1): T2T keys on the source user's Entra objectId, D2C
  // on the normalized SID the device reported. The staged key mirrors that —
  // `manual:<objectId>` for T2T, and the SID itself for D2C, so a D2C add over
  // an identity that also appears in the suggestions feed UPDATES that row
  // rather than rendering a duplicate beside it.
  const handleAddMapping = (source: PendingSource, destination: TenantUser) => {
    if (source.kind === 'tenantUser') {
      const sourceUser = source.user;
      stageEdit(`manual:${sourceUser.id}`, {
        kind: 'set',
        label: sourceUser.displayName ?? sourceUser.userPrincipalName ?? sourceUser.id,
        submission: {
          sourceUserId: sourceUser.id,
          destUserId: destination.id,
          ...(sourceUser.onPremisesSecurityIdentifier
            ? { sourceUserSid: normalizeSid(sourceUser.onPremisesSecurityIdentifier) }
            : {}),
        },
        destUser: destination,
      });
    } else {
      stageEdit(normalizeSid(source.sid), {
        kind: 'set',
        label: source.label,
        submission: {
          sourceUserSid: normalizeSid(source.sid),
          destUserId: destination.id,
        },
        destUser: destination,
      });
    }
    setShowAddPanel(false);
  };

  /**
   * FD-517 — write ONE mapping, now (owner decision, 2026-09-28).
   *
   * The inspector's footer performs a real save rather than staging, so this is
   * a second write *site*. It is deliberately not a second write
   * *implementation*: it calls the same `setIdentityMappings` /
   * `removeIdentityMappings` with a one-element array and reads the same
   * `accepted` / `updated` / `rejected` / `removed` / `notFound` buckets
   * `handleSave` reads, so the FD-ADR-036 freeze surfaces here exactly as it
   * does in a batch — as a per-row rejection carrying the server's own reason.
   * Living beside `handleSave` is the point: a copy in the component is how the
   * two readings of those buckets come to disagree.
   *
   * It UNSTAGES on success. Without that the row it just wrote is still counted
   * by the sticky save bar, and the operator is invited to re-save work that is
   * already on the server.
   *
   * Transport-class failures (consent 409s, GRAPH_ERROR 502s, a whole-request
   * `MAPPINGS_FROZEN`) are thrown, not swallowed — the caller renders them
   * inline, where the operator is looking.
   */
  const saveOne = async (
    key: string,
    edit:
      | { kind: 'set'; submission: MappingSubmission }
      | { kind: 'remove'; sourceKey: string },
  ): Promise<SaveOneOutcome> => {
    if (edit.kind === 'set') {
      const res = await setIdentityMappings(migrationId, [edit.submission]);
      const rejected = res.data.rejected?.[0];
      if (rejected) {
        // Nothing was written, so nothing is unstaged and no query is
        // invalidated — refetching here would only re-read the state the
        // rejection describes.
        return { kind: 'rejected', reason: rejected.reason };
      }
      unstage(key);
      invalidateIdentityQueries(queryClient, migrationId);
      return { kind: (res.data.updated?.length ?? 0) > 0 ? 'updated' : 'accepted' };
    }

    const res = await removeIdentityMappings(migrationId, [edit.sourceKey]);
    unstage(key);
    invalidateIdentityQueries(queryClient, migrationId);
    const removed = (res.data.removed?.length ?? 0) > 0;
    return { kind: removed ? 'removed' : 'notFound' };
  };

  const handleSave = async () => {
    if (isSaving || staged.size === 0) return;
    // Entries, not values — the staged map KEY is what has to be pruned when a
    // chunk lands, so a mid-run failure leaves outstanding work staged and
    // written work gone.
    const entries = [...staged.entries()];
    const setEntries = entries.filter(
      (e): e is [string, Extract<StagedEdit, { kind: 'set' }>] => e[1].kind === 'set',
    );
    const removeEntries = entries.filter(
      (e): e is [string, Extract<StagedEdit, { kind: 'remove' }>] => e[1].kind === 'remove',
    );

    setIsSaving(true);
    setSaveError(null);
    const resultRows: DeviceActionResultRow[] = [];
    const summaryParts: string[] = [];
    const writtenKeys: string[] = [];
    try {
      for (const group of chunk(setEntries, SAVE_CHUNK_SIZE)) {
        const labelByKey = new Map(
          group.map(([, e]) => [submissionKey(e.submission), e.label]),
        );
        const res = await setIdentityMappings(
          migrationId,
          group.map(([, e]) => e.submission),
        );
        for (const key of res.data.accepted ?? []) {
          resultRows.push({
            serialNumber: labelByKey.get(normalizeEchoKey(key)) ?? key,
            result: 'Accepted',
            detail: null,
            intent: 'success',
          });
        }
        for (const key of res.data.updated ?? []) {
          resultRows.push({
            serialNumber: labelByKey.get(normalizeEchoKey(key)) ?? key,
            result: 'Updated',
            detail: null,
            intent: 'success',
          });
        }
        for (const rejected of res.data.rejected ?? []) {
          const echoKey = rejected.sourceUserId ?? rejected.sourceUserSid;
          resultRows.push({
            serialNumber:
              (echoKey && labelByKey.get(normalizeEchoKey(echoKey))) ?? echoKey ?? 'unknown',
            result: 'Rejected',
            detail: rejected.reason,
            intent: 'danger',
          });
        }
        if (res.message) summaryParts.push(res.message);
        // The chunk RETURNED, so it was applied — a per-row rejection inside it
        // is an answer, not a failure, and re-sending it would only re-collect
        // the same rejection.
        for (const [key] of group) writtenKeys.push(key);
      }

      for (const group of chunk(removeEntries, SAVE_CHUNK_SIZE)) {
        const labelByKey = new Map(
          group.map(([, e]) => [normalizeEchoKey(e.sourceKey), e.label]),
        );
        const res = await removeIdentityMappings(
          migrationId,
          group.map(([, e]) => e.sourceKey),
        );
        for (const key of res.data.removed ?? []) {
          resultRows.push({
            serialNumber: labelByKey.get(normalizeEchoKey(key)) ?? key,
            result: 'Removed',
            detail: null,
            intent: 'success',
          });
        }
        for (const key of res.data.notFound ?? []) {
          resultRows.push({
            serialNumber: labelByKey.get(normalizeEchoKey(key)) ?? key,
            result: 'Not found',
            detail: 'No mapping existed for this user.',
            intent: 'warning',
          });
        }
        if (res.message) summaryParts.push(res.message);
        for (const [key] of group) writtenKeys.push(key);
      }

      setSaveResult({
        rows: resultRows,
        summaryMessage: summaryParts.length > 0 ? summaryParts.join(' · ') : null,
      });
      setStaged(new Map());
      // FD-517 — the shared helper, not three calls written out here. FD-513
      // found this set and the CSV import's diverging; the inspector's
      // single-row write is now a FOURTH site, so the set stays in one place.
      invalidateIdentityQueries(queryClient, migrationId);
    } catch (err) {
      // Consent-class 409s and GRAPH_ERROR 502s get dedicated rendering; staged
      // edits are kept so the admin can retry after resolving.
      //
      // FD-510 — but only the ones that did NOT land. A failure on chunk three
      // of five must not imply nothing was written: chunks one and two are on
      // the server, so their edits are pruned from `staged` and their outcomes
      // are surfaced in the result view alongside the error. Reporting a
      // partial write as a total failure is how an operator re-runs work that
      // is already done and then disbelieves the console.
      if (writtenKeys.length > 0) {
        setStaged((prev) => {
          const next = new Map(prev);
          for (const key of writtenKeys) next.delete(key);
          return next;
        });
        setSaveResult({
          rows: resultRows,
          summaryMessage:
            summaryParts.length > 0
              ? summaryParts.join(' · ')
              : `${writtenKeys.length} change${writtenKeys.length === 1 ? '' : 's'} were written before this failure.`,
        });
      }
      setSaveError(err);
    } finally {
      setIsSaving(false);
    }
  };

  // --- view model -----------------------------------------------------------

  // Counts are over ALL rows, so a chip reads the same regardless of which
  // chip is active — a count that changed with the active filter would be
  // unusable for deciding where to go next.
  const filterCounts = useMemo(() => {
    const counts = {} as Record<RowFilter, number>;
    for (const f of FILTER_ORDER) counts[f] = rows.filter((r) => matchesFilter(r, f)).length;
    return counts;
  }, [rows]);

  // FD-495 — the row universe, UNFILTERED. Split out of the old `viewRows`
  // memo so the detail drawer can resolve `?identityRow=` against every row
  // rather than only the ones the active filter shows: a drawer that vanished
  // because the operator changed a filter behind it would look like a defect.
  // The filter itself is unchanged and is applied below.
  const allRowVms = useMemo<RowVM[]>(() => {
    const suggestionRows: RowVM[] = rows.map((row) => ({
      kind: 'suggestion',
      key: normalizeSid(row.sid),
      row,
    }));
    const manualRows: RowVM[] = [...staged.entries()]
      .filter(([key, edit]) => key.startsWith('manual:') && edit.kind === 'set')
      .map(([key, edit]) => {
        const set = edit as Extract<StagedEdit, { kind: 'set' }>;
        return {
          kind: 'manual' as const,
          key,
          // For manual rows the "source user" display comes from the staged
          // label + submission — reconstructed here for rendering only.
          sourceUser: {
            id:
              'sourceUserId' in set.submission && set.submission.sourceUserId
                ? set.submission.sourceUserId
                : key.slice('manual:'.length),
            displayName: set.label,
            userPrincipalName: null,
            mail: null,
            accountEnabled: true,
            onPremisesSamAccountName: null,
            onPremisesSecurityIdentifier:
              'sourceUserSid' in set.submission && set.submission.sourceUserSid
                ? set.submission.sourceUserSid
                : null,
            mailNickname: null,
          },
          staged: set,
        };
      });
    // FD-387 — mappings the suggestions feed does not represent. The server
    // already performs the authoritative source-identity join and hands it back
    // as `existingMapping`, so ANY mapping surfaced that way is excluded here;
    // what remains is genuinely orphaned from the device set. Using the
    // server's own join avoids re-implementing the T2T objectId-vs-SID decode
    // (`Test-IdentityPrincipalMatch`) on the client, which is exactly the
    // representation trap FD-291 documents.
    const surfaced = new Set<string>();
    for (const r of rows) {
      const m = r.existingMapping;
      if (m) surfaced.add((m.sourceUserId ?? m.sourceUserSid ?? '').toLowerCase());
    }
    const mappingRows: RowVM[] = (mappingsQuery.data?.mappings ?? [])
      .filter((m) => {
        const id = (m.sourceUserId ?? m.sourceUserSid ?? '').toLowerCase();
        return id !== '' && !surfaced.has(id);
      })
      .map((m) => ({
        kind: 'mapping' as const,
        key: `mapping:${(m.sourceUserId ?? m.sourceUserSid ?? '').toLowerCase()}`,
        mapping: m,
      }));

    return [...suggestionRows, ...mappingRows, ...manualRows];
  }, [rows, staged, mappingsQuery.data]);

  const viewRows = useMemo<RowVM[]>(
    () => allRowVms.filter((vm) => matchesRowFilter(vm, filter)),
    [allRowVms, filter],
  );

  // FD-384 — a row hidden by a filter must not stay selected. Without this a
  // bulk action would silently act on rows the operator can no longer see,
  // which is the worst version of a bulk action.
  const visibleKeys = useMemo(() => new Set(viewRows.map((vm) => vm.key)), [viewRows]);
  useEffect(() => {
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((k) => visibleKeys.has(k)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleKeys]);

  // FD-495 — the drawer's view model, resolved from the UNFILTERED universe.
  // Built here rather than inside the drawer because the staged-edit map lives
  // here and a staged destination is part of what an operator needs to see:
  // "this row says Contoso because you chose it two minutes ago and have not
  // saved yet" is a different fact from "this row is mapped".
  const detail = useMemo<IdentityDetail | null>(() => {
    if (!selectedRowKey) return null;
    const vm = allRowVms.find((candidate) => candidate.key === selectedRowKey);
    if (!vm) return null;
    const edit = staged.get(vm.key);
    const stagedSet = edit?.kind === 'set' ? edit : null;
    const stagedDestination = stagedSet
      ? {
          displayName: stagedSet.destUser.displayName,
          upn: stagedSet.destUser.userPrincipalName,
          id: stagedSet.destUser.id,
        }
      : null;

    if (vm.kind === 'suggestion') {
      return {
        key: vm.key,
        title: rowLabel(vm.row),
        status: edit ? 'pending' : deriveMappingChipStatus(vm.row),
        row: vm.row,
        mapping: vm.row.existingMapping ?? null,
        stagedDestination,
        stagedRemoval: edit?.kind === 'remove',
        serialNumbers: vm.row.serialNumbers,
        deviceCount: vm.row.deviceCount,
      };
    }
    if (vm.kind === 'mapping') {
      const m = vm.mapping;
      return {
        key: vm.key,
        title:
          m.sourceDisplayName ?? m.sourceUserPrincipalName ?? m.sourceUserId ?? vm.key,
        status: edit ? 'pending' : 'mapped',
        row: null,
        mapping: m,
        stagedDestination,
        stagedRemoval: edit?.kind === 'remove',
        // A mapping-only row has no device by construction — that is exactly
        // why the suggestions feed had nothing to hang it on (FD-387).
        serialNumbers: [],
        deviceCount: 0,
      };
    }
    return {
      key: vm.key,
      title: vm.staged.label,
      status: 'pending',
      row: null,
      mapping: null,
      stagedDestination: {
        displayName: vm.staged.destUser.displayName,
        upn: vm.staged.destUser.userPrincipalName,
        id: vm.staged.destUser.id,
      },
      stagedRemoval: false,
      serialNumbers: [],
      deviceCount: 0,
    };
  }, [selectedRowKey, allRowVms, staged]);

  return {
    // queries
    query,
    mappingsQuery,
    rows,
    devicesWithoutManifest,
    totalUniqueUsers,
    summary,
    matchedUserCount,
    searchInput,
    setSearchInput,
    searchActive: search.length > 0,
    // scope
    isT2T,
    frozen,
    // staged edits
    staged,
    stageEdit,
    unstage,
    discardStaged,
    // FD-517 — the inspector composes its own one-row write, so it needs the
    // SAME submission builder the staged path uses. Two builders is how the
    // T2T objectId/SID pairing and the D2C SID-only shape come to disagree.
    buildSubmission,
    isSaving,
    handleSave,
    saveOne,
    saveResult,
    setSaveResult,
    saveError,
    // filter + selection
    filter,
    setFilter,
    filterCounts,
    selectedKeys,
    setSelectedKeys,
    selectedCount,
    approvableSelected,
    removableSelected,
    acceptableRows,
    bulkNotice,
    setBulkNotice,
    // disclosure state
    expandedDevices,
    toggleDevices,
    expandedAlternates,
    toggleAlternates,
    // surfaces
    showAddPanel,
    setShowAddPanel,
    selectedRowKey,
    setSelectedRowKey,
    detail,
    unmappedObservedSources,
    // rows
    migrationId,
    allRowVms,
    viewRows,
    // actions
    handleAcceptExactMatches,
    handleApprove,
    handleApproveSelected,
    handleRemoveSelected,
    handleRemove,
    handleAddMapping,
  };
}

export type IdentityWorkbench = ReturnType<typeof useIdentityWorkbench>;
